import * as React from 'react';

import { parseZonedDateTime } from '@rematter/common/dateTime';
import { enumAssetTypeSortOption } from '@rematter/frontend-lib-api-client';
import { cn, Select } from '@rematter/ui-web';

import { EntitySelectProps } from './types';

import { createQueryHook } from '~/shared/providers/api';
import { QueryHookResult } from '~/shared/providers/api/createQueryHook';

type SelectableAssetType = QueryHookResult<typeof useAssetTypesQuery>[number];

export const useAssetTypesQuery = createQueryHook(
  'transportation.assetTypes',
  (params: { facilityIds?: string[]; accountAddressIds?: string[] }) => ({
    assetTypes: {
      __args: {
        filter: {
          // ids: params.ids,
          facilityIds: params.facilityIds,
          accountAddressIds: params.accountAddressIds,
        },
        sorting: [enumAssetTypeSortOption.NameAsc],
        pagination: {
          limit: 1000,
          offset: 0,
        },
      },
      entries: {
        id: true,
        name: true,
        description: true,
        capacity: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
    },
  }),
  {
    select: (data) =>
      data?.assetTypes.entries.map((it) => ({
        id: it.id,
        name: it.name,
        description: it.description,
        capacity: it.capacity,
        timestamps: {
          createdAt: parseZonedDateTime(it.timestamps.createdAt),
          updatedAt: parseZonedDateTime(it.timestamps.updatedAt),
        },
      })),
  },
);

type SelectAssetTypeProps = EntitySelectProps<SelectableAssetType> & {
  facilityIds?: string[];
  accountAddressIds?: string[];
};

export const SelectAssetType = ({
  onChange,
  ...props
}: SelectAssetTypeProps) => {
  const { data: assetTypes, isLoading } = useAssetTypesQuery({
    facilityIds: props.facilityIds,
    accountAddressIds: props.accountAddressIds,
  });

  const options = React.useMemo(
    () =>
      (assetTypes ?? []).map((it) => ({
        key: it.id,
        label: it.name,
      })),
    [assetTypes],
  );

  const handleChange = React.useCallback(
    (value: string | undefined) => {
      const assetType = assetTypes?.find((at) => at.id === value);
      onChange?.(value, assetType as SelectableAssetType);
    },
    [assetTypes, onChange],
  );

  return (
    <Select
      {...props}
      onChange={handleChange}
      className={cn('@SelectAssetType', props.className)}
      options={options}
      isLoading={isLoading}
    />
  );
};
